<template>
  <div class="notice-send-management">
    <el-card shadow="never" class="filter-card">
      <div class="card-title">发送消息通知</div>
      <el-form :model="form" label-width="90px">
        <el-form-item label="接收代理">
          <el-select
            v-model="form.agentIds"
            multiple
            filterable
            clearable
            :disabled="!!form.userId"
            placeholder="不选则发给全体用户及全部代理"
            style="width: 100%;"
            @change="onAgentIdsChange"
          >
            <el-option
              v-for="item in agents"
              :key="item.id"
              :label="formatAgentLabel(item)"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="接收用户">
          <el-select
            v-model="form.userId"
            filterable
            remote
            clearable
            :disabled="form.agentIds.length > 0"
            :remote-method="searchUsers"
            :loading="userSearching"
            placeholder="输入用户ID或昵称搜索，与代理互斥"
            style="width: 100%;"
            @clear="onUserClear"
            @change="onUserChange"
          >
            <el-option
              v-for="item in userOptions"
              :key="item.id"
              :label="formatUserLabel(item)"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="消息内容">
          <el-input
            v-model.trim="form.content"
            type="textarea"
            :rows="5"
            maxlength="2000"
            show-word-limit
            placeholder="请输入消息内容"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-s-promotion" :loading="sending" @click="handleSend">
            发送
          </el-button>
          <el-button icon="el-icon-refresh" :disabled="sending" @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card shadow="never" class="table-card">
      <div class="card-title">已发送记录</div>
      <div v-if="error" class="error">{{ error }}</div>
      <el-table
        v-else
        :data="records"
        border
        stripe
        v-loading="loading"
        element-loading-text="正在加载..."
        row-key="id"
      >
        <el-table-column prop="id" label="ID" width="80" align="center" />
        <el-table-column prop="senderName" label="发送人" width="140" show-overflow-tooltip />
        <el-table-column prop="content" label="内容" min-width="280" show-overflow-tooltip />
        <el-table-column prop="createdAt" label="发送时间" width="180" align="center" />
        <el-table-column label="代理转发" width="140" align="center">
          <template slot-scope="scope">
            {{ scope.row.forwardedCount || 0 }} / {{ scope.row.agentCount || 0 }}
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="pagination.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { getNoticeAgents, sendNotice, getAdminNoticePage } from '@/api/notice'
import { get, getResponseMessage } from '@/utils/http'

export default {
  name: 'NoticeSendManagement',
  data() {
    return {
      sending: false,
      loading: false,
      error: '',
      agents: [],
      records: [],
      userOptions: [],
      userSearching: false,
      form: {
        content: '',
        agentIds: [],
        userId: null
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  mounted() {
    this.fetchAgents()
    this.fetchPage()
  },
  methods: {
    formatAgentLabel(item) {
      const roleText = item.role === 1 ? '一级代理' : item.role === 2 ? '二级代理' : '代理'
      return `${item.username}（${roleText}）`
    },
    formatUserLabel(item) {
      const name = item.nickname || item.openid || '-'
      return `ID ${item.id} · ${name}`
    },
    onAgentIdsChange(ids) {
      if (ids && ids.length) {
        this.form.userId = null
        this.userOptions = []
      }
    },
    onUserChange(userId) {
      if (userId) {
        this.form.agentIds = []
      }
    },
    onUserClear() {
      this.form.userId = null
      this.userOptions = []
    },
    async searchUsers(keyword) {
      const text = (keyword || '').trim()
      if (!text) {
        this.userOptions = []
        return
      }
      this.userSearching = true
      try {
        const params = { page: 1, pageSize: 20 }
        if (/^\d+$/.test(text)) {
          params.userId = Number(text)
        } else {
          params.nickname = text
        }
        const result = await get('/admin/users', params)
        const data = result && result.data ? result.data : {}
        this.userOptions = Array.isArray(data.users) ? data.users : []
      } catch (e) {
        this.userOptions = []
      } finally {
        this.userSearching = false
      }
    },
    async fetchAgents() {
      try {
        const result = await getNoticeAgents()
        this.agents = (result && result.data) ? result.data : []
      } catch (e) {
        this.agents = []
      }
    },
    async fetchPage() {
      this.error = ''
      this.loading = true
      try {
        const result = await getAdminNoticePage({
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        })
        const data = result && result.data ? result.data : {}
        this.records = data.records || []
        this.pagination.total = data.total || 0
      } catch (e) {
        this.error = e && e.message ? e.message : '加载失败'
        this.records = []
      } finally {
        this.loading = false
      }
    },
    buildConfirmMessage() {
      if (this.form.userId) {
        const user = this.userOptions.find(item => item.id === this.form.userId)
        const label = user ? this.formatUserLabel(user) : `ID ${this.form.userId}`
        return `确认发送给用户 ${label}？用户将立即收到。`
      }
      if (this.form.agentIds.length) {
        return `确认发送给已选 ${this.form.agentIds.length} 个代理？代理转发后其下级用户才能收到。`
      }
      return '确认发送给全体用户及全部代理？用户立即收到，代理可查看且无需转发。'
    },
    async handleSend() {
      if (!this.form.content) {
        this.$message.warning('请输入消息内容')
        return
      }
      try {
        await this.$confirm(this.buildConfirmMessage(), '发送确认', {
          confirmButtonText: '确认发送',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.sending = true
      try {
        const payload = {
          content: this.form.content
        }
        if (this.form.userId) {
          payload.userId = this.form.userId
        } else {
          payload.agentIds = this.form.agentIds
        }
        const result = await sendNotice(payload)
        this.$message.success(getResponseMessage(result, '发送成功'))
        this.resetForm()
        this.pagination.page = 1
        this.fetchPage()
      } catch (e) {
        this.$message.error(e && e.message ? e.message : '发送失败')
      } finally {
        this.sending = false
      }
    },
    resetForm() {
      this.form.content = ''
      this.form.agentIds = []
      this.form.userId = null
      this.userOptions = []
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchPage()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchPage()
    }
  }
}
</script>

<style scoped>
.filter-card,
.table-card {
  margin-bottom: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}

.error {
  color: #f87171;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
